import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Download,
  FileSpreadsheet,
  Filter,
  History,
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  Save,
  Search,
  Trash2,
  Upload,
  X,
  XCircle,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { PageTitle } from '../../../../_metronic/layout/core';
import { NoticeModal } from '../../datatransfer/metas-unilever-legado/NoticeModal';
import { fetchIdsEcoaApuracao, fetchIdsEcoaHistory, saveIdsEcoaApuracao } from './api';
import { ImportModal } from './ImportModal';
import { HistoryEntry, IDS_ECOA_COLUMNS, IdsEcoaRow, IdsEcoaSkippedRow } from './types';
import {
  createEmptyRow,
  formatMonthLabel,
  getChangedRows,
  getMonthSelectValue,
  getYearOptions,
  getYearSelectValue,
  mergeRowsByIdentity,
  MONTH_OPTIONS,
  normalizeKey,
  parseMonthNumber,
  parseYearNumber,
  prepareRowsForSave,
  validateRows,
  validationErrorLines,
} from './utils';

type NoticeState = {
  title: string;
  message?: string;
  details?: string[];
  variant?: 'success' | 'danger' | 'info' | 'confirm';
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm?: () => void | Promise<void>;
  onCancel?: () => void;
};

function hydrateRows(rows: IdsEcoaRow[]) {
  return rows.map((row) => ({
    ...row,
    mesReferencia: row.mesReferencia === null || row.mesReferencia === undefined ? '' : String(row.mesReferencia),
    anoReferencia: row.anoReferencia === null || row.anoReferencia === undefined ? String(new Date().getFullYear()) : String(row.anoReferencia),
  }));
}

function formatDateTime(value?: string | null) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
}

function actionLabel(action: string) {
  if (action === 'insert') return 'Inclusão';
  if (action === 'update') return 'Alteração';
  if (action === 'delete') return 'Remoção';
  return 'Alteração';
}

type HistoryModalProps = {
  isOpen: boolean;
  loading: boolean;
  history: HistoryEntry[];
  onClose: () => void;
};

const HistoryModal: React.FC<HistoryModalProps> = ({ isOpen, loading, history, onClose }) => {
  const [visible, setVisible] = React.useState(false);

  React.useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => setVisible(true));
    } else {
      setVisible(false);
    }
  }, [isOpen]);

  React.useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center px-4 transition-all duration-200 ${
        visible ? 'opacity-100' : 'opacity-0'
      }`}
      role="dialog"
      aria-modal="true"
      aria-label="Histórico de IDs E-COA"
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div
        className={`relative flex max-h-[90vh] w-full max-w-5xl flex-col rounded-2xl bg-white shadow-2xl transition-all duration-200 ${
          visible ? 'scale-100 translate-y-0' : 'scale-95 translate-y-3'
        }`}
      >
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Histórico</h2>
            <p className="mt-0.5 text-xs text-gray-500">Últimas alterações em IDs E-COA - Apuração</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
            aria-label="Fechar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 className="mb-3 h-8 w-8 animate-spin text-blue-500" />
              <p className="text-sm text-gray-500">Carregando histórico...</p>
            </div>
          ) : history.length === 0 ? (
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-8 text-center text-sm text-gray-500">
              Nenhum histórico encontrado
            </div>
          ) : (
            <div className="space-y-4">
              {history.map((entry) => {
                const changes = entry.data?.changes || [];
                const skipped = entry.data?.skipped || [];
                const isSkippedLog = skipped.length > 0 && changes.length === 0;
                return (
                  <div key={entry.id_log} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                    <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{entry.name || entry.email || 'Usuário'}</p>
                        <p className="text-xs text-gray-500">{formatDateTime(entry.created_at)}</p>
                      </div>
                      <span className={`rounded-full px-3 py-1 text-xs font-semibold ${isSkippedLog ? 'bg-amber-50 text-amber-700' : 'bg-blue-50 text-blue-700'}`}>
                        {isSkippedLog
                          ? `${skipped.length} ${skipped.length === 1 ? 'linha ignorada' : 'linhas ignoradas'}`
                          : `${changes.length} ${changes.length === 1 ? 'alteração' : 'alterações'}`}
                      </span>
                    </div>

                    <div className="max-h-64 overflow-auto rounded-xl border border-gray-100">
                      {isSkippedLog ? (
                        <table className="w-full min-w-[760px] text-xs">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left font-semibold text-gray-500">Linha</th>
                              <th className="px-3 py-2 text-left font-semibold text-gray-500">ID Regra</th>
                              <th className="px-3 py-2 text-left font-semibold text-gray-500">Motivo</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {skipped.map((row, index) => (
                              <tr key={`${entry.id_log}-skipped-${row.row}-${index}`}>
                                <td className="px-3 py-2 font-mono text-gray-700">{row.row}</td>
                                <td className="px-3 py-2 font-mono text-gray-700">{row.idRegra || '-'}</td>
                                <td className="px-3 py-2 text-gray-700">{row.reason}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <table className="w-full min-w-[760px] text-xs">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left font-semibold text-gray-500">Ação</th>
                              <th className="px-3 py-2 text-left font-semibold text-gray-500">ID Regra</th>
                              <th className="px-3 py-2 text-left font-semibold text-gray-500">Campos</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {changes.map((change, index) => (
                              <tr key={`${entry.id_log}-${change.guid}-${index}`}>
                                <td className="px-3 py-2 text-gray-700">{actionLabel(change.action)}</td>
                                <td className="px-3 py-2 font-mono text-gray-700">{change.idRegra}</td>
                                <td className="px-3 py-2 text-gray-700">
                                  {change.fields.map((field) => field.label).join(', ')}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const YEAR_OPTIONS = getYearOptions();

const IdsEcoaApuracaoScreen: React.FC = () => {
  const [rows, setRows] = useState<IdsEcoaRow[]>([]);
  const [originalRows, setOriginalRows] = useState<IdsEcoaRow[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [monthFilter, setMonthFilter] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [notice, setNotice] = useState<NoticeState | null>(null);
  const [noticeLoading, setNoticeLoading] = useState(false);
  const rowsRef = useRef<IdsEcoaRow[]>([]);

  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  const closeNotice = useCallback(() => {
    if (noticeLoading) return;
    setNotice(null);
  }, [noticeLoading]);

  const openNotice = useCallback((nextNotice: NoticeState) => {
    setNotice(nextNotice);
  }, []);

  const loadRows = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetchIdsEcoaApuracao();
      if (!response.success) {
        openNotice({
          title: 'Não foi possível carregar',
          message: response.error || 'Tente novamente em alguns instantes.',
          variant: 'danger',
        });
        return;
      }

      const hydratedRows = hydrateRows(response.data?.rows || []);
      setRows(hydratedRows);
      setOriginalRows(hydratedRows);
    } catch {
      openNotice({
        title: 'Não foi possível carregar',
        message: 'Falha na comunicação com o servidor.',
        variant: 'danger',
      });
    } finally {
      setIsLoading(false);
    }
  }, [openNotice]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const originalGuidSet = useMemo(() => new Set(originalRows.map((row) => row.guid)), [originalRows]);
  const currentGuidSet = useMemo(() => new Set(rows.map((row) => row.guid)), [rows]);
  const changedRows = useMemo(() => getChangedRows(rows, originalRows), [rows, originalRows]);
  const removedCount = useMemo(
    () => originalRows.filter((row) => !currentGuidSet.has(row.guid)).length,
    [originalRows, currentGuidSet]
  );
  const pendingCount = changedRows.length + removedCount;
  const changedGuids = useMemo(() => new Set(changedRows.map((row) => row.guid)), [changedRows]);

  const indexedRows = useMemo(() => {
    return rows.map((row) => ({
      row,
      searchKey: normalizeKey([
        row.idRegra,
        row.nomePromocao,
        formatMonthLabel(row.mesReferencia),
        row.anoReferencia,
      ].join(' ')),
      monthValue: parseMonthNumber(row.mesReferencia),
      yearValue: parseYearNumber(row.anoReferencia),
    }));
  }, [rows]);

  const hasActiveFilters = Boolean(searchTerm || monthFilter || yearFilter);

  const filteredRows = useMemo(() => {
    const term = normalizeKey(searchTerm);
    const selectedMonth = parseMonthNumber(monthFilter);
    const selectedYear = parseYearNumber(yearFilter);

    return indexedRows.filter(({ searchKey, monthValue, yearValue }) => {
      if (term && !searchKey.includes(term)) return false;
      if (monthFilter && monthValue !== selectedMonth) return false;
      if (yearFilter && yearValue !== selectedYear) return false;
      return true;
    }).map(({ row }) => row);
  }, [indexedRows, searchTerm, monthFilter, yearFilter]);

  const pageCount = useMemo(
    () => Math.max(1, Math.ceil(filteredRows.length / pageSize)),
    [filteredRows.length, pageSize]
  );
  const safeCurrentPage = Math.min(currentPage, pageCount);
  const paginatedRows = useMemo(() => {
    const start = (safeCurrentPage - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, pageSize, safeCurrentPage]);
  const visibleStart = filteredRows.length === 0 ? 0 : ((safeCurrentPage - 1) * pageSize) + 1;
  const visibleEnd = Math.min(safeCurrentPage * pageSize, filteredRows.length);

  useEffect(() => {
    if (currentPage > pageCount) setCurrentPage(pageCount);
  }, [currentPage, pageCount]);

  const saveRows = async (
    nextRows: IdsEcoaRow[],
    source: 'table' | 'import',
    action: string,
    skippedRows: IdsEcoaSkippedRow[] = []
  ) => {
    const preparedRows = prepareRowsForSave(nextRows);
    const localErrors = validateRows(preparedRows);

    if (localErrors.length > 0) {
      openNotice({
        title: 'Dados inválidos',
        message: 'Revise as linhas indicadas e tente novamente.',
        details: validationErrorLines(localErrors),
        variant: 'danger',
      });
      return false;
    }

    setIsSaving(true);
    const response = await saveIdsEcoaApuracao(preparedRows, source, action, skippedRows);
    setIsSaving(false);

    if (!response.success) {
      openNotice({
        title: 'Dados inválidos',
        message: response.error || 'Revise as linhas indicadas e tente novamente.',
        details: validationErrorLines(response.validationErrors || []),
        variant: 'danger',
      });
      return false;
    }

    const savedRows = hydrateRows(response.data?.rows || preparedRows);
    setRows(savedRows);
    setOriginalRows(savedRows);
    openNotice({
      title: 'IDs E-COA salvos',
      message: `${response.changedCount || 0} ${response.changedCount === 1 ? 'registro alterado' : 'registros alterados'}${response.skippedCount ? ` e ${response.skippedCount} ${response.skippedCount === 1 ? 'linha ignorada' : 'linhas ignoradas'}` : ''}.`,
      variant: 'success',
    });
    return true;
  };

  const handleCellChange = useCallback((guid: string, key: keyof Omit<IdsEcoaRow, 'guid'>, value: string) => {
    setRows((currentRows) => {
      let changed = false;
      const nextRows = currentRows.map((row) => {
        if (row.guid !== guid) return row;
        if (String(row[key] ?? '') === String(value ?? '')) return row;
        changed = true;
        return { ...row, [key]: value };
      });

      return changed ? nextRows : currentRows;
    });
  }, []);

  const handleAddRow = useCallback(() => {
    setRows((currentRows) => {
      const nextRows = [...currentRows, createEmptyRow()];
      setCurrentPage(Math.max(1, Math.ceil(nextRows.length / pageSize)));
      return nextRows;
    });
  }, [pageSize]);

  const handleRemoveRow = useCallback((guid: string) => {
    const row = rowsRef.current.find((item) => item.guid === guid);
    const isPersisted = originalGuidSet.has(guid);

    if (!isPersisted) {
      setRows((currentRows) => currentRows.filter((item) => item.guid !== guid));
      return;
    }

    openNotice({
      title: 'Remover linha',
      message: `O ID ${row?.idRegra || ''} ficará pendente até salvar as alterações.`,
      variant: 'confirm',
      confirmLabel: 'Remover',
      cancelLabel: 'Cancelar',
      onConfirm: () => {
        setRows((currentRows) => currentRows.filter((item) => item.guid !== guid));
        setNotice(null);
      },
    });
  }, [openNotice, originalGuidSet]);

  const handleTableSave = () => {
    if (pendingCount === 0) return;

    openNotice({
      title: 'Salvar alterações',
      message: `${pendingCount} ${pendingCount === 1 ? 'linha pendente' : 'linhas pendentes'} serão validadas antes de salvar.`,
      variant: 'confirm',
      confirmLabel: 'Salvar',
      cancelLabel: 'Cancelar',
      onConfirm: async () => {
        setNoticeLoading(true);
        await saveRows(rows, 'table', 'table_save');
        setNoticeLoading(false);
      },
    });
  };

  const handleImportRows = async (importedRows: IdsEcoaRow[], skippedRows: IdsEcoaSkippedRow[] = []) => {
    const nextRows = mergeRowsByIdentity(rows, importedRows);
    return saveRows(nextRows, 'import', skippedRows.length > 0 ? 'xlsx_import_with_skipped' : 'xlsx_import', skippedRows);
  };

  const handleExportXlsx = () => {
    if (isLoading || isSaving || filteredRows.length === 0) return;

    try {
      const worksheet = XLSX.utils.aoa_to_sheet([
        IDS_ECOA_COLUMNS.map((column) => column.label),
        ...filteredRows.map((row) => IDS_ECOA_COLUMNS.map((column) => row[column.key] ?? '')),
      ]);
      worksheet['!cols'] = [{ wch: 24 }, { wch: 60 }, { wch: 18 }, { wch: 18 }];
      worksheet['!autofilter'] = { ref: worksheet['!ref']! };

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'IDs E-COA - Apuração');
      XLSX.writeFile(workbook, `ids-ecoa-apuracao-${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch {
      openNotice({
        title: 'Não foi possível exportar',
        message: 'Tente novamente em alguns instantes.',
        variant: 'danger',
      });
    }
  };

  const handleOpenHistory = async () => {
    setHistoryOpen(true);
    setHistoryLoading(true);
    try {
      const historyRows = await fetchIdsEcoaHistory();
      setHistory(historyRows);
    } catch {
      setHistory([]);
      openNotice({
        title: 'Não foi possível carregar o histórico',
        message: 'Tente novamente em alguns instantes.',
        variant: 'danger',
      });
    } finally {
      setHistoryLoading(false);
    }
  };

  const clearFilters = useCallback(() => {
    setSearchTerm('');
    setMonthFilter('');
    setYearFilter('');
    setCurrentPage(1);
  }, []);

  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  }, []);

  const handleMonthFilterChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    setMonthFilter(event.target.value);
    setCurrentPage(1);
  }, []);

  const handleYearFilterChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    setYearFilter(event.target.value);
    setCurrentPage(1);
  }, []);

  const handlePageSizeChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    setPageSize(Number(event.target.value));
    setCurrentPage(1);
  }, []);

  const goToPreviousPage = useCallback(() => {
    setCurrentPage((page) => Math.max(1, page - 1));
  }, []);

  const goToNextPage = useCallback(() => {
    setCurrentPage((page) => Math.min(pageCount, page + 1));
  }, [pageCount]);

  return (
    <>
      <PageTitle>Unilever - IDs E-COA - Apuração</PageTitle>
      <div className="min-h-screen bg-gray-50">
        <div className="mx-auto px-4 py-8 sm:px-6 lg:px-8">
          <header className="mb-6">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h1 className="text-2xl font-bold tracking-tight text-gray-900">IDs E-COA - Apuração</h1>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={loadRows}
                  disabled={isLoading}
                  className="inline-flex h-10 items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
                  aria-label="Recarregar"
                  title="Recarregar"
                >
                  <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                </button>
                <button
                  type="button"
                  onClick={handleAddRow}
                  className="inline-flex h-10 items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                >
                  <Plus className="h-4 w-4" />
                  Inserir linha
                </button>
                <button
                  type="button"
                  onClick={() => setImportOpen(true)}
                  className="inline-flex h-10 items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                >
                  <Upload className="h-4 w-4" />
                  Importar XLSX
                </button>
                <button
                  type="button"
                  onClick={handleExportXlsx}
                  disabled={isLoading || isSaving || filteredRows.length === 0}
                  title="Exportar todos os registros dos filtros atuais, incluindo todas as páginas"
                  className="inline-flex h-10 items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Download className="h-4 w-4" />
                  Exportar XLSX
                </button>
                <button
                  type="button"
                  onClick={handleOpenHistory}
                  className="inline-flex h-10 items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                >
                  <History className="h-4 w-4" />
                  Histórico
                </button>
                <button
                  type="button"
                  onClick={handleTableSave}
                  disabled={pendingCount === 0 || isSaving}
                  className="inline-flex h-10 items-center gap-2 rounded-xl bg-blue-600 px-5 text-sm font-medium text-white shadow-sm shadow-blue-600/20 transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none"
                >
                  {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Salvar alterações
                </button>
              </div>
            </div>
          </header>

          <div className="mb-5 grid gap-4 sm:grid-cols-3">
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-blue-50 p-2">
                  <FileSpreadsheet className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xl font-bold text-gray-900">
                    {filteredRows.length}
                    {hasActiveFilters && <span className="text-sm font-semibold text-gray-400"> / {rows.length}</span>}
                  </p>
                  <p className="text-xs text-gray-500">{hasActiveFilters ? 'Linhas filtradas' : 'Linhas cadastradas'}</p>
                </div>
              </div>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-amber-50 p-2">
                  <Save className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-xl font-bold text-gray-900">{pendingCount}</p>
                  <p className="text-xs text-gray-500">Pendentes</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-5 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <div className="rounded-lg bg-gray-100 p-2">
                  <Filter className="h-4 w-4 text-gray-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">Filtros</p>
                  <p className="text-xs text-gray-500">{filteredRows.length} de {rows.length} registros</p>
                </div>
              </div>

              <button
                type="button"
                onClick={clearFilters}
                disabled={!hasActiveFilters}
                className="inline-flex h-9 items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <XCircle className="h-3.5 w-3.5" />
                Limpar filtros
              </button>
            </div>

            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-[minmax(280px,1.6fr)_minmax(150px,0.8fr)_minmax(130px,0.7fr)]">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  placeholder="Buscar por ID Regra ou nome da promoção"
                  className="h-11 w-full rounded-xl border border-gray-200 bg-white pl-10 pr-4 text-sm text-gray-800 outline-none transition-all focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20"
                />
              </div>

              <select
                value={monthFilter}
                onChange={handleMonthFilterChange}
                className="h-11 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-800 outline-none transition-all focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20"
              >
                <option value="">Todos os meses</option>
                {MONTH_OPTIONS.map((month) => (
                  <option key={month.value} value={month.value}>{month.label}</option>
                ))}
              </select>

              <select
                value={yearFilter}
                onChange={handleYearFilterChange}
                className="h-11 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-800 outline-none transition-all focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20"
              >
                <option value="">Todos os anos</option>
                {YEAR_OPTIONS.map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          </div>

          {isLoading ? (
            <div className="flex flex-col items-center justify-center rounded-xl border border-gray-200 bg-white p-16 shadow-sm">
              <Loader2 className="mb-3 h-8 w-8 animate-spin text-blue-500" />
              <p className="text-sm text-gray-500">Carregando IDs E-COA...</p>
            </div>
          ) : (
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
              <div className="overflow-x-auto">
                <table className="min-w-[980px] w-full divide-y divide-gray-200">
                  <thead>
                    <tr className="bg-gray-50">
                      {IDS_ECOA_COLUMNS.map((column) => (
                        <th
                          key={column.key}
                          scope="col"
                          className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500"
                        >
                          <div className="flex items-center gap-1.5">
                            <Pencil className="h-3 w-3 text-blue-500" />
                            {column.label}
                            {column.required && <span className="text-red-500">*</span>}
                          </div>
                        </th>
                      ))}
                      <th scope="col" className="w-14 px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {paginatedRows.map((row) => {
                      const isChanged = changedGuids.has(row.guid);
                      return (
                        <tr
                          key={row.guid}
                          className={`transition-colors ${isChanged ? 'bg-blue-50/60 hover:bg-blue-50' : 'hover:bg-gray-50'}`}
                        >
                          <td className="px-4 py-3 align-middle">
                            <input
                              type="text"
                              value={row.idRegra || ''}
                              onChange={(event) => handleCellChange(row.guid, 'idRegra', event.target.value)}
                              className={`h-9 w-full rounded-lg border px-3 text-sm font-mono text-gray-800 outline-none transition-all focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 ${
                                isChanged ? 'border-blue-200 bg-white' : 'border-transparent bg-transparent hover:border-gray-200 hover:bg-white'
                              }`}
                              placeholder="-"
                            />
                          </td>
                          <td className="px-4 py-3 align-middle">
                            <input
                              type="text"
                              value={row.nomePromocao || ''}
                              onChange={(event) => handleCellChange(row.guid, 'nomePromocao', event.target.value)}
                              className={`h-9 w-full rounded-lg border px-3 text-sm text-gray-800 outline-none transition-all focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 ${
                                isChanged ? 'border-blue-200 bg-white' : 'border-transparent bg-transparent hover:border-gray-200 hover:bg-white'
                              }`}
                              placeholder="-"
                            />
                          </td>
                          <td className="px-4 py-3 align-middle">
                            <select
                              value={getMonthSelectValue(row.mesReferencia)}
                              onChange={(event) => handleCellChange(row.guid, 'mesReferencia', event.target.value)}
                              className={`h-9 w-full rounded-lg border px-3 text-sm text-gray-800 outline-none transition-all focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 ${
                                isChanged ? 'border-blue-200 bg-white' : 'border-transparent bg-transparent hover:border-gray-200 hover:bg-white'
                              }`}
                            >
                              <option value="">-</option>
                              {MONTH_OPTIONS.map((month) => (
                                <option key={month.value} value={month.value}>{month.label}</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-4 py-3 align-middle">
                            <select
                              value={getYearSelectValue(row.anoReferencia)}
                              onChange={(event) => handleCellChange(row.guid, 'anoReferencia', event.target.value)}
                              className={`h-9 w-full rounded-lg border px-3 text-sm text-gray-800 outline-none transition-all focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 ${
                                isChanged ? 'border-blue-200 bg-white' : 'border-transparent bg-transparent hover:border-gray-200 hover:bg-white'
                              }`}
                            >
                              <option value="">Atual</option>
                              {YEAR_OPTIONS.map((year) => (
                                <option key={year} value={year}>{year}</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-4 py-3 text-right align-middle">
                            <button
                              type="button"
                              onClick={() => handleRemoveRow(row.guid)}
                              className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"
                              title="Remover linha"
                              aria-label="Remover linha"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {paginatedRows.length === 0 && (
                  <div className="flex flex-col items-center justify-center gap-2 py-16">
                    <div className="rounded-full bg-gray-100 p-4">
                      <FileSpreadsheet className="h-7 w-7 text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-500">Nenhum ID E-COA cadastrado</p>
                  </div>
                )}
              </div>

              {filteredRows.length > 0 && (
                <div className="flex flex-col gap-3 border-t border-gray-200 bg-gray-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-xs text-gray-500">
                    Mostrando {visibleStart}-{visibleEnd} de {filteredRows.length} registros filtrados
                  </p>

                  <div className="flex flex-wrap items-center gap-3">
                    <label className="flex items-center gap-2 text-xs font-medium text-gray-600">
                      Linhas por página
                      <select
                        value={pageSize}
                        onChange={handlePageSizeChange}
                        className="h-9 rounded-lg border border-gray-200 bg-white px-2 text-xs text-gray-700 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20"
                      >
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                        <option value={250}>250</option>
                        <option value={500}>500</option>
                      </select>
                    </label>

                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={goToPreviousPage}
                        disabled={safeCurrentPage <= 1}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                        aria-label="Página anterior"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </button>
                      <span className="min-w-[88px] text-center text-xs font-semibold text-gray-600">
                        {safeCurrentPage} de {pageCount}
                      </span>
                      <button
                        type="button"
                        onClick={goToNextPage}
                        disabled={safeCurrentPage >= pageCount}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                        aria-label="Próxima página"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <ImportModal
        isOpen={importOpen}
        existingRows={rows}
        onClose={() => setImportOpen(false)}
        onImport={handleImportRows}
      />

      <HistoryModal
        isOpen={historyOpen}
        loading={historyLoading}
        history={history}
        onClose={() => setHistoryOpen(false)}
      />

      <NoticeModal
        isOpen={notice !== null}
        title={notice?.title || ''}
        message={notice?.message}
        details={notice?.details}
        variant={notice?.variant}
        confirmLabel={notice?.confirmLabel}
        cancelLabel={notice?.cancelLabel}
        loading={noticeLoading || isSaving}
        onConfirm={notice?.onConfirm}
        onCancel={notice?.onCancel}
        onClose={closeNotice}
      />
    </>
  );
};

export default IdsEcoaApuracaoScreen;
